import forOwn from "lodash-es/forOwn";
import isArray from "lodash-es/isArray";
import isPlainObject from "lodash-es/isPlainObject";

import { Day, dayISORegex, parseDate } from "../../../core/model/types";
import { parseNumber } from "../../../core/utils/numberUtils";

import type { CountryCode, CountrySubdivision } from "../../../core/model/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeResponse(response: any) {
	if (isArray(response)) {
		response.forEach((value, index) => normalizeElement(response, index, value));
	} else {
		forOwn(response, (value, key) => normalizeElement(response, key, value));
	}
	return response;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeElement(data: any, key: string | number, value: any) {
	if (value === null) {
		data[key] = undefined;
	} else if (isPlainObject(value) || isArray(value)) {
		normalizeResponse(value);
	}
}

type NormalizePair = { type: string; key: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertByKeys(data: any, ...keys: NormalizePair[]) {
	keys.forEach(({ type, key }) => convertByKey(data, type, key.split(".")));

	return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertByKey(object: any, type: string, parts: string[]) {
	if (object === undefined) {
		return;
	}
	if (isArray(object)) {
		object.forEach(element => convertByKey(element, type, parts));
		return;
	}

	const [head, ...tail] = parts;
	const subObject = object[head];
	if (tail.length === 0) {
		if (isArray(subObject)) {
			object[head] = subObject.map(element => convertValue(type, element));
		} else {
			object[head] = convertValue(type, subObject);
		}
	} else {
		if (isArray(subObject)) {
			subObject.forEach(element => convertByKey(element, type, tail));
		} else {
			convertByKey(subObject, type, tail);
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertValue(type: string, value: any) {
	switch (type) {
		case "Day":
			if (value) {
				// NOTE: value's format which is returned from server has to be ISO8601
				if (typeof value === "string" && dayISORegex.test(value)) {
					const parts = value.split(/\D/);
					return new Day(parts[0], +parts[1] - 1, parts[2]);
				} else {
					throw new Error(`${value}'s format has to be ISO8601`);
				}
			}
			return undefined;
		case "Date":
			return value === undefined ? undefined : parseDate(value, { iso: true });
		case "Number":
			return parseNumber(value);
		case "CountryCode":
			return value as CountryCode;
		case "CountrySubdivision":
			return value as CountrySubdivision;
		default:
			throw new Error(`${type} is not supported yet`);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function denormalizeData(data: any) {
	if (isArray(data)) {
		data.forEach((value, index) => denormalizeElement(data, index, value));
	} else {
		forOwn(data, (value, key) => denormalizeElement(data, key, value));
	}
	return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function denormalizeElement(data: any, key: string | number, value: any) {
	if (value instanceof Day) {
		data[key] = value.toISOString();
	} else if (value instanceof Date) {
		data[key] = value.toISOString();
	} else if (isPlainObject(value) || isArray(value)) {
		denormalizeData(value);
	}
}
